import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './print.css';
import './responsive-fixes.css';
import { checkBootLoop, clearBootRing } from './bootGuard';
import { clearSession } from './utils/session';

const root = ReactDOM.createRoot(document.getElementById('root'));

/**
 * Static, network-free panel used by the boot-loop breaker and by the lazy-load
 * failure path. Same inline styles everywhere: buttons keep a >=44px min-height
 * and 16px font.
 */
function renderStaticPanel({ title, body, buttons }) {
  const buttonStyle = {
    minHeight: '44px',
    padding: '10px 20px',
    fontSize: '16px',
    cursor: 'pointer',
    borderRadius: '6px',
    border: '1px solid #9ca3af',
    backgroundColor: '#ffffff',
    color: '#111827',
  };

  root.render(
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f9fafb',
      color: '#111827',
      padding: '24px',
    }}>
      <div style={{
        maxWidth: '480px',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '32px',
        textAlign: 'center',
      }}>
        <h1 style={{ fontSize: '20px', margin: '0 0 16px 0' }}>
          {title}
        </h1>
        <p style={{ fontSize: '16px', margin: '0 0 24px 0', color: '#4b5563' }}>
          {body}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {buttons.map((button) => (
            <button
              key={button.label}
              type="button"
              style={button.primary
                ? { ...buttonStyle, backgroundColor: '#1d4ed8', color: '#ffffff', border: '1px solid #1d4ed8' }
                : buttonStyle}
              onClick={button.onClick}
            >
              {button.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Rendered when the boot-loop breaker trips. Static panel only:
 * - no App import (App pulls in analytics and the CSRF prefetch, both of
 *   which make network calls on import),
 * - no ThemeProvider (its effect can fetch /api/tenants/public/{slug}),
 * - no network requests while tripped.
 */
function renderTrippedPanel() {
  const handleTryAgain = () => {
    clearBootRing();
    window.location.reload();
  };

  const handleClearSessionAndReload = () => {
    try {
      // utils/session is side-effect-free on import, so this is safe here.
      clearSession();
    } catch (error) {
      console.error('Failed to clear session:', error);
    }
    clearBootRing();
    window.location.reload();
  };

  renderStaticPanel({
    title: 'This page reloaded itself repeatedly and was stopped to protect the server.',
    body: 'If this keeps happening, clear the session and sign in again.',
    buttons: [
      { label: 'Try again', onClick: handleTryAgain },
      { label: 'Clear session and reload', primary: true, onClick: handleClearSessionAndReload },
    ],
  });
}

function boot() {
  // Dynamic imports keep App's module-level side effects (analytics session
  // start, CSRF prefetch) from firing when the breaker is tripped.
  Promise.all([import('./App'), import('./context/ThemeContext')])
    .then(([appModule, themeModule]) => {
      const App = appModule.default;
      const { ThemeProvider } = themeModule;
      root.render(
        <React.StrictMode>
          <ThemeProvider>
            <App />
          </ThemeProvider>
        </React.StrictMode>
      );
    })
    .catch((error) => {
      console.error('Failed to load the app:', error);
      // Static panel only — no network work from here (same rule as the
      // tripped panel above).
      renderStaticPanel({
        title: "Couldn't load the app",
        body: 'A code update is probably being applied right now — try reloading.',
        buttons: [
          { label: 'Reload', onClick: () => window.location.reload() },
        ],
      });
    });
}

// Boot only while the document is visible. Safari 27's Reading List metadata
// fetcher loads pages in a hidden web view in a tight loop (~every 2s),
// sharing localStorage with the real tab; evaluating App's module graph on
// every such load costs ~2 requests per iteration against production. The
// fetcher's view never becomes visible, so it never boots. A real background
// tab (⌘-click) starts hidden too, so it boots on first show instead.
if (checkBootLoop()) {
  renderTrippedPanel();
} else if (document.visibilityState === 'visible') {
  boot();
} else {
  const bootOnFirstVisible = () => {
    if (document.visibilityState !== 'visible') return;
    document.removeEventListener('visibilitychange', bootOnFirstVisible);
    boot();
  };
  document.addEventListener('visibilitychange', bootOnFirstVisible);
}
